import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  getCompanyMetricsSeries,
  getMetricLabelOptions,
  type CompanyMetricChangeDisplay,
  type CompanyMetricSeriesItemDto,
  type CompanyMetricsView,
  type MetricCategory,
} from '@/services/metrics.service'
import {
  DEFAULT_FINANCIAL_METRIC_CARD_LABELS,
  EMPTY_FINANCIAL_METRIC_CARD_SELECTION,
  FINANCIALS_CARD_ORDER,
  type FinancialMetricCardKey,
  type FinancialMetricCardSelection,
} from '@/features/company-overview/metric-card-model'

export {
  DEFAULT_FINANCIAL_METRIC_CARD_LABELS,
  EMPTY_FINANCIAL_METRIC_CARD_SELECTION,
  type FinancialMetricCardKey,
  type FinancialMetricCardSelection,
} from '@/features/company-overview/metric-card-model'

export type FinancialMetricOption = {
  id: string
  label: string
  labeledAs: string
  category?: MetricCategory
  sectionName?: string
  sortOrder?: number | null
}

export interface MetricPoint {
  quarter: string
  quarterKey: string
  periodEndDate: string
  value: number | null
}

export type CompanyMetricSeries = {
  metricLabelId: string
  labeledAs: string
  latestValue: number | null
  comparisonValue: number | null
  changePercent: number | null
  changeDisplay: CompanyMetricChangeDisplay
  comparisonQuarter?: string | null
  points: MetricPoint[]
}

const CARD_ORDER = FINANCIALS_CARD_ORDER

function toMetricOptionLabel(
  labeledAs: string,
  metricTypeName?: string
): string {
  if (labeledAs === 'Adjusted EBITDA') return 'Adj. EBITDA'
  return metricTypeName ?? labeledAs
}

function dedupeMetricLabelIds(ids: string[]): string[] {
  const seen = new Set<string>()
  const deduped: string[] = []

  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    deduped.push(id)
  }

  return deduped
}

function toSeriesMapById(
  series: CompanyMetricSeriesItemDto[]
): Map<string, CompanyMetricSeries> {
  return new Map(
    series.map((item) => [
      item.metricLabelId,
      {
        metricLabelId: item.metricLabelId,
        labeledAs: item.labeledAs,
        latestValue: item.latestValue,
        comparisonValue: item.comparisonValue,
        changePercent: item.changePercent,
        changeDisplay: item.changeDisplay,
        comparisonQuarter: item.comparisonQuarter ?? null,
        points: item.points,
      },
    ])
  )
}

function toSeriesMapByLabel(
  series: CompanyMetricSeriesItemDto[]
): Map<string, CompanyMetricSeries> {
  const map = new Map<string, CompanyMetricSeries>()

  for (const item of series) {
    if (map.has(item.labeledAs)) continue
    map.set(item.labeledAs, {
      metricLabelId: item.metricLabelId,
      labeledAs: item.labeledAs,
      latestValue: item.latestValue,
      comparisonValue: item.comparisonValue,
      changePercent: item.changePercent,
      changeDisplay: item.changeDisplay,
      comparisonQuarter: item.comparisonQuarter ?? null,
      points: item.points,
    })
  }

  return map
}

export function useCompanyMetricCards(
  companyId: string,
  selection: FinancialMetricCardSelection = EMPTY_FINANCIAL_METRIC_CARD_SELECTION,
  cardOrder: readonly FinancialMetricCardKey[] = CARD_ORDER
) {
  const selectedMetricLabelIds = useMemo(() => {
    const selectedIds = cardOrder
      .map((key) => selection[key])
      .filter((id): id is string => Boolean(id))
    return dedupeMetricLabelIds(selectedIds).sort()
  }, [cardOrder, selection])

  const defaultQuery = useQuery({
    queryKey: [
      'company-metric-series',
      companyId,
      {
        mode: 'defaults',
        periodType: 'LTM',
        scenarioType: 'ACTUAL',
        quarters: 4,
        changeBasis: 'YOY',
      },
    ],
    queryFn: () =>
      getCompanyMetricsSeries(companyId, {
        periodType: 'LTM',
        scenarioType: 'ACTUAL',
        quarters: 4,
        changeBasis: 'YOY',
      }),
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
  })

  const customQuery = useQuery({
    queryKey: [
      'company-metric-series',
      companyId,
      {
        mode: 'custom',
        metricLabelIds: selectedMetricLabelIds,
        periodType: 'LTM',
        scenarioType: 'ACTUAL',
        quarters: 4,
        changeBasis: 'YOY',
      },
    ],
    queryFn: () =>
      getCompanyMetricsSeries(companyId, {
        metricLabelIds:
          selectedMetricLabelIds.length > 0
            ? selectedMetricLabelIds
            : undefined,
        periodType: 'LTM',
        scenarioType: 'ACTUAL',
        quarters: 4,
        changeBasis: 'YOY',
      }),
    enabled: Boolean(companyId) && selectedMetricLabelIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })

  const defaultSeries = defaultQuery.data?.data ?? []
  const customSeries = customQuery.data?.data ?? []
  const defaultByLabel = toSeriesMapByLabel(defaultSeries)
  const customById = toSeriesMapById(customSeries)
  const customQueryEnabled = selectedMetricLabelIds.length > 0

  const cards: Partial<Record<FinancialMetricCardKey, CompanyMetricSeries>> = {}
  const status: Record<
    FinancialMetricCardKey,
    { isLoading: boolean; isError: boolean }
  > = {
    revenue: { isLoading: false, isError: false },
    grossProfit: { isLoading: false, isError: false },
    ebitda: { isLoading: false, isError: false },
    cash: { isLoading: false, isError: false },
    netIncome: { isLoading: false, isError: false },
  }

  const customQueryResolved = customQueryEnabled && !customQuery.isLoading

  cardOrder.forEach((key) => {
    const defaultLabel = DEFAULT_FINANCIAL_METRIC_CARD_LABELS[key]
    const selectedMetricLabelId = selection[key]
    const seriesForCard = selectedMetricLabelId
      ? (customById.get(selectedMetricLabelId) ??
        (customQueryResolved ? undefined : defaultByLabel.get(defaultLabel)))
      : defaultByLabel.get(defaultLabel)

    if (seriesForCard) {
      cards[key] = seriesForCard
    }

    const isDefaultCardLoading =
      defaultQuery.isLoading && !defaultByLabel.get(defaultLabel)
    const isSelectedCardLoading = selectedMetricLabelId
      ? customQueryEnabled &&
        customQuery.isFetching &&
        !customById.get(selectedMetricLabelId)
      : false

    status[key] = {
      isLoading: isDefaultCardLoading || isSelectedCardLoading,
      isError:
        defaultQuery.isError ||
        (Boolean(selectedMetricLabelId) &&
          customQueryEnabled &&
          customQuery.isError),
    }
  })

  return {
    cards,
    status,
    isError:
      defaultQuery.isError || (customQueryEnabled && customQuery.isError),
  }
}

export type ExpandedPeriodType = 'LTM' | 'QUARTERLY' | 'MONTHLY' | 'YTD'

const EXPANDED_PERIOD_PARAMS: Record<
  ExpandedPeriodType,
  { periodType: ExpandedPeriodType; quarters: number; months?: number }
> = {
  MONTHLY: { periodType: 'MONTHLY', quarters: 8, months: 24 },
  QUARTERLY: { periodType: 'QUARTERLY', quarters: 8 },
  LTM: { periodType: 'LTM', quarters: 8 },
  YTD: { periodType: 'YTD', quarters: 12 },
}

const EXPANDED_VIEW_PARAMS: Record<
  CompanyMetricsView,
  { quarters?: number; months?: number; years?: number }
> = {
  MONTHLY: { months: 24 },
  QUARTERLY: { quarters: 8 },
  ANNUAL: { years: 5 },
}

export function useExpandedMetricSeries(
  companyId: string,
  metricLabelId: string | undefined,
  periodType: ExpandedPeriodType = 'LTM',
  enabled: boolean = false,
  view?: CompanyMetricsView
) {
  const periodParams = EXPANDED_PERIOD_PARAMS[periodType]
  const viewParams = view ? EXPANDED_VIEW_PARAMS[view] : undefined

  // When view is set, the column cadence is decoupled from periodType: counts
  // come from the view, not the period (matching the financials table).
  const quarters = viewParams ? viewParams.quarters : periodParams.quarters
  const months = viewParams ? viewParams.months : periodParams.months
  const years = viewParams?.years

  const query = useQuery({
    queryKey: [
      'company-metric-series',
      companyId,
      {
        mode: 'expanded',
        metricLabelId,
        periodType: periodParams.periodType,
        quarters,
        months,
        years,
        view,
      },
    ],
    queryFn: () =>
      getCompanyMetricsSeries(companyId, {
        metricLabelIds: metricLabelId ? [metricLabelId] : undefined,
        periodType: periodParams.periodType,
        scenarioType: 'ACTUAL',
        quarters,
        months,
        years,
        view,
        changeBasis: 'YOY',
      }),
    enabled: enabled && Boolean(companyId) && Boolean(metricLabelId),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })

  const series = useMemo(() => {
    const data = query.data?.data ?? []
    if (!metricLabelId) return undefined
    return data.find((item) => item.metricLabelId === metricLabelId)
  }, [query.data, metricLabelId])

  return useMemo(
    () => ({
      series: series
        ? {
            metricLabelId: series.metricLabelId,
            labeledAs: series.labeledAs,
            latestValue: series.latestValue,
            comparisonValue: series.comparisonValue,
            changePercent: series.changePercent,
            changeDisplay: series.changeDisplay,
            comparisonQuarter: series.comparisonQuarter ?? null,
            points: series.points,
          }
        : undefined,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isError: query.isError,
    }),
    [series, query.isLoading, query.isFetching, query.isError]
  )
}

export function useCompanyMetricLabelOptions(companyId: string) {
  return useQuery({
    queryKey: ['company-metric-label-options', companyId],
    queryFn: () =>
      // No periodType filter: balance-sheet labels only exist as point-in-time
      // (QUARTERLY/MONTHLY) rows, so filtering by LTM would hide them entirely.
      // Surfacing every label with data lets the picker show all options; the
      // chart's trailing-period selector then governs which periodType is
      // queried for the actual series data.
      getMetricLabelOptions({
        companyId,
        scenarioType: 'ACTUAL',
      }),
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
    select: (response) =>
      (response.data ?? []).map((opt) => ({
        id: opt.id,
        labeledAs: opt.labeledAs,
        label: toMetricOptionLabel(opt.labeledAs, opt.metricTypeName),
        category: opt.metricTypeCategory,
        sectionName: opt.globalSectionName,
        sortOrder: opt.sortOrder ?? null,
      })),
  })
}

export { CARD_ORDER }
